/**
 * 003 — tab shell of /cashflow. Period, account and tab live in the URL
 * (validateSearch in routes/cashflow.tsx). The statement import panel is the
 * prototype's, unchanged; every tab receives the same BankTabProps.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, GlassPanel, Input, PageHeader, TabNav } from '../ui';
import { api } from '../../services/api';
import { bankApi, bankKeys, ibanLabel, money, quarterOf } from '../../lib/bank-api';
import { errorText } from './ReviewEditor';
import { OverviewTab } from './OverviewTab';
import { TransactionsTab } from './TransactionsTab';
import { RecurringTab } from './RecurringTab';
import { IstQuarterTab } from './IstQuarterTab';
import { CoverageTab } from './CoverageTab';
import { ForecastTab } from './ForecastTab';
import { PartnersPanel } from './PartnersPanel';
import type { BankTabId, BankTabProps } from './types';

export type CashflowSearch = {
  tab: BankTabId;
  from: string;
  to: string;
  account?: string;
  basis: 'invoiceDate' | 'paymentDate';
  quarter: string;
};

const TABS: { id: BankTabId; label: string }[] = [
  { id: 'uebersicht', label: 'Übersicht' },
  { id: 'buchungen', label: 'Bankbuchungen' },
  { id: 'lastschriften', label: 'Lastschriften ohne Beleg' },
  { id: 'ist', label: 'Quartal nach Zahlung' },
  { id: 'abdeckung', label: 'Abdeckung' },
  { id: 'prognose', label: 'Liquiditätsprognose' },
  { id: 'partner', label: 'Gesellschafter & Regeln' },
];

type FileInput = { title: string; pdfBase64: string };
type Preview = {
  previewHash: string;
  errors: string[];
  newStatements: number;
  newTransactions: number;
  existingStatements: number;
  duplicateFiles: number;
  statements: {
    periodStart: string;
    periodEnd: string;
    openingCents: number;
    closingCents: number;
    rows: number;
    incomingCents: number;
    outgoingCents: number;
  }[];
};

function ImportPanel({ onNotice }: { onNotice: (m: string) => void }) {
  const [files, setFiles] = useState<FileInput[]>([]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [reading, setReading] = useState(false);
  const client = useQueryClient();
  const importMutation = useMutation({
    mutationFn: async (commit: boolean) =>
      api.post<Preview>(`/bank/imports/${commit ? 'commit' : 'preview'}`, {
        files,
        ...(commit && preview ? { previewHash: preview.previewHash } : {}),
      }),
    onSuccess: (data, commit) => {
      setPreview(data);
      if (commit) {
        onNotice('Import abgeschlossen. Originale und Provenienz gesichert.');
        void client.invalidateQueries({ queryKey: bankKeys.all });
      }
    },
    onError: (error) => onNotice(errorText(error)),
  });
  async function chooseFiles(list: globalThis.FileList | null) {
    if (!list) return;
    setReading(true);
    onNotice('');
    setPreview(null);
    try {
      const result: FileInput[] = [];
      for (const file of Array.from(list)) {
        if (file.size > 5_000_000) throw new Error('Maximal 5 MB pro PDF');
        const pdfBase64 = await new Promise<string>((resolve, reject) => {
          const reader = new globalThis.FileReader();
          reader.onload = () => resolve(String(reader.result).split(',')[1]!);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        result.push({ title: file.name, pdfBase64 });
      }
      setFiles(result);
    } catch (error) {
      onNotice(errorText(error));
    } finally {
      setReading(false);
    }
  }
  return (
    <GlassPanel className="bank-no-print">
      <details>
        <summary className="cursor-pointer font-medium">Originalauszüge importieren</summary>
        <p className="my-3 text-sm">
          EUR-Auszüge der Deutschen Bank. Original-PDFs werden erneut gelesen; Beträge, tatsächliche Perioden, Dubletten
          und Saldoübergänge werden vor dem Import geprüft. Der Import wird immer von Ihnen übernommen.
        </p>
        <input
          aria-label="Original-PDF-Auszüge"
          type="file"
          accept="application/pdf"
          multiple
          onChange={(e) => void chooseFiles(e.target.files)}
        />
        <div className="mt-3 flex gap-3">
          <Button disabled={!files.length || reading || importMutation.isPending} onClick={() => importMutation.mutate(false)}>
            Vorschau prüfen ({files.length})
          </Button>
          <Button
            disabled={!preview || preview.errors.length > 0 || importMutation.isPending}
            onClick={() => importMutation.mutate(true)}
          >
            Validierten Import übernehmen
          </Button>
        </div>
        {preview && (
          <div className="mt-3">
            <p>
              {preview.newStatements} neue Auszüge · {preview.newTransactions} neue Buchungen ·{' '}
              {preview.existingStatements} bereits importiert · {preview.duplicateFiles} Dateidubletten
            </p>
            {preview.errors.map((e, i) => (
              <p role="alert" key={i}>
                {e}
              </p>
            ))}
            <ul>
              {preview.statements.map((s) => (
                <li key={s.periodStart}>
                  {s.periodStart} – {s.periodEnd}: {s.rows} Buchungen, {money(s.openingCents)} + {money(s.incomingCents)} −{' '}
                  {money(s.outgoingCents)} = {money(s.closingCents)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </details>
    </GlassPanel>
  );
}

export function CashflowTabs({
  search,
  onSearch,
  tenantId,
  role,
}: {
  search: CashflowSearch;
  onSearch: (patch: Partial<CashflowSearch>) => void;
  tenantId: string | undefined;
  role: string | null | undefined;
}) {
  const [notice, setNotice] = useState('');
  const coverage = useQuery({
    queryKey: bankKeys.coverage(tenantId, search.from, search.to),
    queryFn: () => bankApi.coverage({ from: search.from, to: search.to }),
    enabled: Boolean(tenantId),
  });
  const accounts = coverage.data?.accounts.map((a) => a.account) ?? [];
  const props: BankTabProps = {
    tenantId,
    from: search.from,
    to: search.to,
    account: search.account,
    quarter: search.quarter,
    basis: search.basis,
    canManage: role === 'geschäftsführer',
    onNotice: setNotice,
    onQuarterChange: (quarter) => onSearch({ quarter }),
  };
  const setQuarter = (q: number) => {
    const year = Number(search.from.slice(0, 4));
    const from = `${year}-${String((q - 1) * 3 + 1).padStart(2, '0')}-01`;
    const to = new Date(Date.UTC(year, q * 3, 0)).toISOString().slice(0, 10);
    onSearch({ from, to, quarter: quarterOf(from) });
  };
  return (
    <div className="space-y-6 bank-print">
      <style>{`@media print{html,body,#root{height:auto!important;overflow:visible!important} body *{visibility:hidden;overflow:visible!important;max-height:none!important} .bank-print,.bank-print *{visibility:visible;color:#000!important;background:transparent!important;box-shadow:none!important;border-color:#bbb!important;opacity:1!important;transform:none!important;backdrop-filter:none!important} .bank-print{position:absolute;left:0;top:0;width:100%;color:black;background:white} .bank-no-print{display:none!important} .bank-print section,.bank-print table{break-inside:auto} .bank-print tr{break-inside:avoid} .bank-print .overflow-x-auto{overflow:visible}}`}</style>
      <PageHeader
        title="Cashflow & Bankabgleich"
        subtitle="Tatsächlicher Kontofluss aus validierten Originalauszügen · Abgleich mit Belegen · keine steuerliche Freigabe"
      />
      <GlassPanel className="bank-no-print">
        <div className="flex flex-wrap items-end gap-4">
          <label className="text-sm">
            Von
            <Input type="date" value={search.from} onChange={(e) => onSearch({ from: e.target.value })} />
          </label>
          <label className="text-sm">
            Bis
            <Input type="date" value={search.to} onChange={(e) => onSearch({ to: e.target.value })} />
          </label>
          <label className="text-sm">
            Konto
            <select
              aria-label="Konto"
              data-testid="bank-account-select"
              className="block rounded border border-white/10 bg-surface p-2"
              value={search.account ?? ''}
              onChange={(e) => onSearch({ account: e.target.value || undefined })}
            >
              <option value="">Alle Konten</option>
              {accounts.map((a) => (
                <option key={a} value={a}>
                  {ibanLabel(a)}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            Belegübersicht
            <select
              className="block rounded border border-white/10 bg-surface p-2"
              value={search.basis}
              onChange={(e) => onSearch({ basis: e.target.value as CashflowSearch['basis'] })}
            >
              <option value="invoiceDate">Rechnungsdatum</option>
              <option value="paymentDate">Bekannter Zahlungstag</option>
            </select>
          </label>
          <div className="flex gap-2">
            {[1, 2, 3, 4].map((q) => (
              <Button key={q} onClick={() => setQuarter(q)}>
                Q{q}
              </Button>
            ))}
          </div>
          <Button onClick={() => globalThis.window.print()}>Drucken</Button>
        </div>
        <p className="mt-3 text-sm text-white/70">
          Zeitraum: {search.from} bis {search.to}. Bankachse: Buchungstag. Keine steuerliche Freigabe oder Steuerberechnung.
        </p>
      </GlassPanel>
      {notice && (
        <p role="status" className="rounded border border-amber-400/40 p-3">
          {notice}
        </p>
      )}
      <ImportPanel onNotice={setNotice} />
      <TabNav
        aria-label="Cashflow-Bereiche"
        items={TABS.map((t) => ({ id: t.id, label: t.label, testid: `bank-tab-${t.id}` }))}
        active={search.tab}
        onChange={(id) => onSearch({ tab: id as BankTabId })}
        className="bank-no-print"
      />
      {search.tab === 'uebersicht' && <OverviewTab {...props} />}
      {search.tab === 'buchungen' && <TransactionsTab {...props} />}
      {search.tab === 'lastschriften' && <RecurringTab {...props} />}
      {search.tab === 'ist' && <IstQuarterTab {...props} />}
      {search.tab === 'abdeckung' && <CoverageTab {...props} />}
      {search.tab === 'prognose' && <ForecastTab {...props} />}
      {search.tab === 'partner' && <PartnersPanel {...props} />}
    </div>
  );
}
